import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
    ClientManagerInstanceToken,
    ClientManagerInterface,
    CONNECTION_ERROR_CODES,
    EncodingUtilities,
    ENDPOINTS,
    getUuidV4,
    NotificationInterface,
    OnlineOnlyService,
    SYNC_REQUEST_TYPE,
    SynchronizationInstanceToken,
    SynchronizationInterface,
    SyncQueueService,
    UriUtilitiesService,
    UserMasterInstanceToken,
    UserMasterInterface,
    ConfigInstanceToken,
    ConfigInterface,
} from '@evorait/pace-core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { Observable, of, throwError } from 'rxjs';
import { catchError, concatAll, filter, map, mergeMap, tap } from 'rxjs/operators';
import { NotificationInstanceToken } from '../core/interfaces/notification.interface';
import { FormState } from '../store/state/form.state';
import { FormIdentifier } from '../types/form-identifier';
import { FormsConstants } from '../types/forms-constants';
import { FUNCTIONAL_ELEMENT_TYPE } from '../types/functional-element';
import { ParticipantCopyId } from '../types/participant-copy';
import { Signature } from '../types/signature';
import { FormUtilities } from '../utilities/form-utilities';
import { NotificationUtilities } from '../utilities/notification-utilities';
import { ODocBoxUploadModel } from './docBox-odata.interface';
import {
    FormsWriteInterface,
    ODataRefreshXMLPayload,
    ODataReleaseLockPayload,
    ODataSubmitFormPayload,
    ODataSubmitParticipantCopyPayload,
} from './forms-write.interface';
import { OdataFormsService } from './odata-forms.service';
import { XmlParserService } from './xml-parser.service';
import { XmlSerializerService } from './xml-serializer.service';
import FORMTYPE = FormsConstants.FORMTYPE;

@Injectable({
    providedIn: 'root',
})
export class FormsWriteWithPaceOfflineService implements FormsWriteInterface {
    public constructor(
        @Inject(LogInstanceToken) protected readonly log: LogInterface,
        @Inject(ConfigInstanceToken) protected readonly configService: ConfigInterface,
        @Inject(SynchronizationInstanceToken) protected readonly synchronizationService: SynchronizationInterface,
        protected readonly syncQueueService: SyncQueueService,
        @Inject(UserMasterInstanceToken) protected readonly userMasterService: UserMasterInterface,
        @Inject(NotificationInstanceToken) protected readonly notificationService: NotificationInterface,
        @Inject(ClientManagerInstanceToken) protected readonly clientManager: ClientManagerInterface,
        protected readonly xmlParserService: XmlParserService,
        protected readonly xmlSerializerService: XmlSerializerService,
        protected readonly uriUtilities: UriUtilitiesService,
        protected readonly onlineOnlyService: OnlineOnlyService,
        private readonly httpClient: HttpClient,
        private readonly odataFormsService: OdataFormsService,
    ) {}

    /**
     * Saves a completely or partially filled out form back to the gateway.
     * Saving is a four-step process:
     * First the XML content is sent to the gateway. Once it was received all necessary signature images are trans-
     * mitted.
     *
     * Signatures are generally sent with the savemode set to "I" for "interim".
     * If the protocol should be saved as final protocol, only the last signature that gets transmitted has the
     * savemode set to "F" for final.
     *
     * After sending the signatures, the protocol is completely transmitted and the lock can be released on the
     * gateway.
     *
     * Since all the requests are buffered in the offline store, we request a flush after all this to actually send
     * the data to the backend.
     *
     * @param form the form to be submitted
     * @param saveMode the save mode to be used for the submission
     * @returns {Observable<void | CONNECTION_ERROR_CODES>} observable that emits when the form was submitted
     * @throws error there is no notification defined.
     */
    public submitMaster(
        form: FormState,
        saveMode: FormsConstants.SUBMIT_STATUS,
    ): Observable<void | CONNECTION_ERROR_CODES> {
        const notification = this.notificationService.getNotificationStateByIdentifier(form.notification);
        if (typeof notification === 'undefined') {
            throw new Error(
                `FormsWriteService: submitMaster: Could not load notification '${JSON.stringify(
                    form.notification,
                )}'  for form '${form.formId}'`,
            );
        }

        const equipment = this.notificationService.getMainEquipmentFromNotification(notification);
        if (typeof equipment === 'undefined') {
            throw new Error(
                `FormsWriteService: submitMaster: Notification '${JSON.stringify(
                    form.notification,
                )}' has no main equipment!`,
            );
        }

        this.log.debug(
            `FormsWriteService: submitMaster: Submitting form '${
                form.formId.formId
            }' for notification '${NotificationUtilities.toString(form.notification)}'`,
        );

        const user = this.userMasterService.getSelectedUser();

        const formQuery = FormUtilities.isServicePartQuestionnaire(form.type)
            ? ENDPOINTS.F4MSubmitSpqSet
            : ENDPOINTS.F4mSubmitFormSet;

        // clean the XML and retrieve it
        this.xmlParserService.setXml(form.participantCopies[FormsConstants.MASTER_PARTICIPANT_ID].xml);
        this.xmlSerializerService.cleanMasterXml(saveMode, form.type);
        const cleanedXml = this.xmlParserService.getXml(form.doctype);

        // restore XML of currently viewed participant
        this.xmlParserService.setXml(form.participantCopies[form.selectedParticipantCopyId].xml);

        // encode the XML for transfer with base64
        const encodedXml = EncodingUtilities.base64EncodeUnicode(cleanedXml);

        const employeeSignatureFunctionalElementType = FormUtilities.isAstoriaForm(form.type)
            ? FUNCTIONAL_ELEMENT_TYPE.SIGNOFF_EMPLOYEE
            : FUNCTIONAL_ELEMENT_TYPE.EMPLOYEE_SIGNATURE;
        const employeeSignature =
            form.supportParticipantCopies ||
            form.type === FormsConstants.FORMTYPE.TRAININGCHECKLIST ||
            (FormUtilities.isCosimaForm(form.type) && !form.systemCondition?.employeeSignature)
                ? (Object.values(form.participantCopies[FormsConstants.MASTER_PARTICIPANT_ID].functionalElements).find(
                      (fe) => fe.type === employeeSignatureFunctionalElementType,
                  ) as Signature | undefined)
                : form.systemCondition?.employeeSignature;
        if (typeof employeeSignature === 'undefined') {
            this.log.warn(`FormsWriteService: submitMaster: No employee signature found for form '${form.formId}'`);
        }

        const customerSignatureFunctionalElementType = FormUtilities.isAstoriaForm(form.type)
            ? FUNCTIONAL_ELEMENT_TYPE.SIGNOFF_CUSTOMER
            : FUNCTIONAL_ELEMENT_TYPE.APPROVER_SIGNATURE;
        const customerSignature =
            form.supportParticipantCopies ||
            form.type === FormsConstants.FORMTYPE.TRAININGCHECKLIST ||
            (FormUtilities.isCosimaForm(form.type) && !form.systemCondition?.customerSignature)
                ? (Object.values(form.participantCopies[FormsConstants.MASTER_PARTICIPANT_ID].functionalElements).find(
                      (fe) => fe.type === customerSignatureFunctionalElementType,
                  ) as Signature | undefined)
                : form.systemCondition?.customerSignature;
        if (typeof customerSignature === 'undefined') {
            this.log.warn(`FormsWriteService: submitMaster: No customer signature found for form '${form.formId}'`);
        }

        const selectedRecipients = form.recipients.filter((r) => r.selected);

        const employeeSignatureString = employeeSignature?.signature ?? '';
        const customerSignatureString = customerSignature?.signature ?? '';

        const payload: ODataSubmitFormPayload = {
            EquipmentId: form.formId.equipmentId,
            FormDocid: form.formId.formId,
            GID: user.frontId,
            Savemode: saveMode,
            XMLContent: encodedXml,
            Country: user.country,
            CompanyCode: user.salesOrg,
            NotificationNo: form.notification.qmNum,
            TaskNumber: form.notification.maNum,
            NotificationType: notification.notificationType,
            TechIdentNum: equipment.tidnr,
            SerialNum: equipment.sernr,
            CSESignatureFileName: employeeSignature?.entityPath ?? '',
            CSEMimeType: FormUtilities.isAstoriaForm(form.type)
                ? FormsConstants.MIME_TYPE.EPS
                : FormsConstants.MIME_TYPE.PNG,
            CSEBase64: employeeSignatureString.length > 0 ? employeeSignatureString : FormsConstants.EMPTY_SIGNATURE,
            CustomerSignatureFileName: customerSignature?.entityPath ?? '',
            CustomerMimeType: FormUtilities.isAstoriaForm(form.type)
                ? FormsConstants.MIME_TYPE.EPS
                : FormsConstants.MIME_TYPE.PNG,
            CustomerBase64:
                customerSignatureString.length > 0 ? customerSignatureString : FormsConstants.EMPTY_SIGNATURE,
            EquiShortDesc: equipment.equiShortDesc,

            Email_01:
                (form.type === FORMTYPE.TRAININGCHECKLIST && form.supportParticipantCopies) ||
                FormUtilities.isServicePartQuestionnaire(form.type)
                    ? this.configService.getCseEmailFax()
                    : selectedRecipients[0].email,
            Email_02:
                form.type === FORMTYPE.TRAININGCHECKLIST && form.supportParticipantCopies
                    ? notification.address.fax1
                    : (selectedRecipients[1]?.email ?? ''),
            Email_03:
                form.type === FORMTYPE.TRAININGCHECKLIST && form.supportParticipantCopies
                    ? notification.address.fax2
                    : (selectedRecipients[2]?.email ?? ''),
            Email_04:
                form.type === FORMTYPE.TRAININGCHECKLIST && form.supportParticipantCopies
                    ? notification.address.fax3
                    : (selectedRecipients[3]?.email ?? ''),
            Email_05:
                form.type === FORMTYPE.TRAININGCHECKLIST && form.supportParticipantCopies
                    ? notification.address.fax4
                    : (selectedRecipients[4]?.email ?? ''),
            Email_06:
                form.type === FORMTYPE.TRAININGCHECKLIST && form.supportParticipantCopies
                    ? form.participantCopies[FormsConstants.MASTER_PARTICIPANT_ID].email
                    : (selectedRecipients[5]?.email ?? ''),
            Email_07:
                form.type === FORMTYPE.TRAININGCHECKLIST && form.supportParticipantCopies
                    ? ''
                    : (selectedRecipients[6]?.email ?? ''),
            Email_08:
                form.type === FORMTYPE.TRAININGCHECKLIST && form.supportParticipantCopies
                    ? ''
                    : (selectedRecipients[7]?.email ?? ''),
            Email_09:
                form.type === FORMTYPE.TRAININGCHECKLIST && form.supportParticipantCopies
                    ? ''
                    : (selectedRecipients[8]?.email ?? ''),
            Email_10:
                form.type === FORMTYPE.TRAININGCHECKLIST && form.supportParticipantCopies
                    ? ''
                    : (selectedRecipients[9]?.email ?? ''),
            Material: FormUtilities.isServicePartQuestionnaire(form.type) ? equipment.matnr : undefined,
            ComponentMaterialNumber:
                form.participantCopies[FormsConstants.MASTER_PARTICIPANT_ID].defectServicePartIdentification?.materialNo
                    .value,
            ComponentSerialNumber:
                form.participantCopies[FormsConstants.MASTER_PARTICIPANT_ID].defectServicePartIdentification?.serialNo
                    .value,
        };

        if (this.configService.getEnableManualIndividualization()) {
            payload.ManIndividualizationComponents = form.individualization ? form.manIndividualizationComponents : [];
        }

        const identifier = `${payload.FormDocid}-${payload.NotificationNo}-${payload.TaskNumber}-${getUuidV4()}`;

        return this.submitForm(formQuery, payload, identifier).pipe(
            map(() => {}),
            catchError((error: ODataRequestError | undefined) => {
                const requestError = error;

                if (requestError !== undefined) {
                    this.log.error(
                        `FormsWriteService: submitMaster: Error saving form on gateway ${requestError.response.body} for form '${form.formId}'`,
                    );
                } else {
                    this.log.error(
                        `FormsWriteService: submitMaster: Error saving form on gateway for form '${form.formId}'`,
                    );
                }

                // re-throw error
                return throwError(() => requestError);
            }),
        );
    }

    public submitParticipantCopies(form: FormState, participantCopyIds: ParticipantCopyId[]): Observable<void> {
        const notification = this.notificationService.getNotificationStateByIdentifier(form.notification);

        if (typeof notification === 'undefined') {
            throw new Error(
                `FormsWriteService: submitParticipantCopies: Could not load notification '${JSON.stringify(
                    form.notification,
                )}'`,
            );
        }

        const equipment = this.notificationService.getMainEquipmentFromNotification(notification);
        if (typeof equipment === 'undefined') {
            throw new Error(
                `FormsWriteService: submitParticipantCopies: Notification '${JSON.stringify(
                    form.notification,
                )}' has no main equipment!`,
            );
        }
        this.log.debug(
            `FormsWriteService: submitParticipantCopies: Submitting form '${
                form.formId.formId
            }' for notification '${NotificationUtilities.toString(
                form.notification,
            )}' for ids ${participantCopyIds.join(' ')}`,
        );

        const user = this.userMasterService.getSelectedUser();

        const formQuery = ENDPOINTS.F4mSubmitParticipantCopySet;

        const submit$ = participantCopyIds.map((participantCopyId) => {
            const participantCopy = form.participantCopies[participantCopyId];

            // clean the XML and retrieve it
            this.xmlParserService.setXml(form.participantCopies[participantCopyId].xml);
            this.xmlSerializerService.cleanParticipantXml(
                form.checkpoints,
                form.type,
                form.participantCopies[participantCopyId].options,
                form.participantCopies[participantCopyId].functionalElements,
            );
            const cleanedXml = this.xmlParserService.getXml(form.doctype);

            // encode the XML for transfer with base64
            const encodedXml = EncodingUtilities.base64EncodeUnicode(cleanedXml);

            const employeeSignatureIdentifier = FormUtilities.isCosimaForm(form.type)
                ? FUNCTIONAL_ELEMENT_TYPE.EMPLOYEE_SIGNATURE
                : FUNCTIONAL_ELEMENT_TYPE.SIGNOFF_EMPLOYEE;
            const employeeSignature = Object.values(participantCopy.functionalElements).find(
                (fe) => fe.type === employeeSignatureIdentifier,
            ) as Signature | undefined;
            if (typeof employeeSignature === 'undefined') {
                this.log.warn(`FormsWriteService: submitParticipantCopies: No employee signature found!`);
            }

            const customerSignatureIdentifier = FormUtilities.isCosimaForm(form.type)
                ? FUNCTIONAL_ELEMENT_TYPE.APPROVER_SIGNATURE
                : FUNCTIONAL_ELEMENT_TYPE.SIGNOFF_CUSTOMER;
            const customerSignature = Object.values(participantCopy.functionalElements).find(
                (fe) => fe.type === customerSignatureIdentifier,
            ) as Signature | undefined;

            if (typeof customerSignature === 'undefined') {
                this.log.warn(`FormsWriteService: submitParticipantCopies: No customer signature found!`);
            }

            const payload: ODataSubmitParticipantCopyPayload = {
                EquipmentId: form.formId.equipmentId,
                FormDocid: form.formId.formId,
                GID: user.frontId,
                XMLContent: encodedXml,
                Country: user.country,
                CompanyCode: user.salesOrg,
                NotificationNo: form.notification.qmNum,
                TaskNumber: form.notification.maNum,
                NotificationType: notification.notificationType,
                TechIdentNum: equipment.tidnr,
                SerialNum: equipment.sernr,
                CSESignatureFileName: employeeSignature?.entityPath ?? '',
                CSEMimeType: FormUtilities.isAstoriaForm(form.type)
                    ? FormsConstants.MIME_TYPE.EPS
                    : FormsConstants.MIME_TYPE.PNG,
                CSEBase64: employeeSignature?.signature ?? FormsConstants.EMPTY_SIGNATURE,
                CustomerSignatureFileName: customerSignature?.entityPath ?? '',
                CustomerMimeType: FormUtilities.isAstoriaForm(form.type)
                    ? FormsConstants.MIME_TYPE.EPS
                    : FormsConstants.MIME_TYPE.PNG,
                CustomerBase64: customerSignature?.signature ?? FormsConstants.EMPTY_SIGNATURE,
                ParticipantEmail: participantCopy.email,
                ParticipantName: `${participantCopy.firstName} ${participantCopy.lastName}`,
                EquiShortDesc: equipment.equiShortDesc,
                UniqueID: participantCopy.id,
            };

            const identifier = `${payload.UniqueID}-${payload.FormDocid}-${payload.NotificationNo}-${
                payload.TaskNumber
            }-${getUuidV4()}`;

            return this.submitForm(formQuery, payload, identifier);
        });

        // restore XML of currently viewed participant
        this.xmlParserService.setXml(form.participantCopies[form.selectedParticipantCopyId].xml);

        // concatAll serializes an array of observables and allows us to serialize the write access to the offline
        // store. But it emits an event each time an inner observable emits. This would trigger the entire pipeline
        // and therefore dispatch the success action in the calling effect multiple times, one time for each participant
        // copy that was successfully submitted.
        // Since we are only interested in the overall success of the submits, we pass a custom string in as element
        // to indicate when all submit observables are complete. We then filter for that string to block the individual
        // events from triggering the rest of the pipe and let only the final completion event trigger the rest of the
        // pipe.
        return submit$.length > 0
            ? of(...submit$, of('submit of participant copies done')).pipe(
                  concatAll(),
                  filter((result) => result === 'submit of participant copies done'),
                  tap(() =>
                      this.log.debug(
                          `FormsWriteService: submitParticipantCopies: submitted participant copies for form ${form.formId.formId}`,
                      ),
                  ),
                  map(() => {}),
                  catchError((error: ODataRequestError | undefined) => {
                      const requestError = error;
                      if (requestError !== undefined) {
                          this.log.error(
                              `FormsWriteService: submitParticipantCopies: Error saving form on gateway ${requestError.response.body}`,
                          );
                      } else {
                          this.log.error(`FormsWriteService: submitParticipantCopies: Error saving form on gateway`);
                      }

                      // re-throw error
                      return throwError(() => requestError);
                  }),
              )
            : of(undefined);
    }

    /**
     * Send the "release lock" command to make the form lockable and editable for somebody else again.
     * Flush the offline store to actually send the data to the backend.
     *
     * @param id the form identifier
     * @returns {Observable<void | CONNECTION_ERROR_CODES>} observable that emits when the lock was released
     */
    public releaseLock(id: FormIdentifier): Observable<void | CONNECTION_ERROR_CODES> {
        const query = ENDPOINTS.F4mReleaseLock;

        const payload: ODataReleaseLockPayload = {
            EquipmentId: id.equipmentId,
            FormDocid: id.formId,
        };

        this.log.debug(
            `FormsWriteService: releaseLock: Calling ${ENDPOINTS.F4mReleaseLock} with payload ${JSON.stringify(
                payload,
            )}`,
        );

        return this.postRequest(query, payload).pipe(
            mergeMap(() => this.synchronizationService.sendOfflineData()),
            catchError((error) => {
                this.log.error('FormsWriteService: releaseLock: no internet connection, add sync request');
                this.syncQueueService.addSyncRequest(SYNC_REQUEST_TYPE.FLUSH_ONLY);
                return throwError(() => `FormsWriteService: releaseLock: error: ${JSON.stringify(error)}`);
            }),
        );
    }

    public refreshXML(form: FormIdentifier): Observable<unknown> {
        const query = ENDPOINTS.RefreshXML;
        const payload: ODataRefreshXMLPayload = {
            EquipmentId: form.equipmentId,
            FormDocid: form.formId,
        };

        return this.postRequest(query, payload).pipe(
            catchError((error) => {
                return throwError(
                    () =>
                        `FormsWriteService: refreshXML: error: ${JSON.stringify(error, Object.getOwnPropertyNames(error))}`,
                );
            }),
        );
    }

    protected postRequest(url: string, payload: ODataReleaseLockPayload | ODataRefreshXMLPayload): Observable<unknown> {
        const headers = new HttpHeaders({
            'Content-Type': 'application/json; charset=UTF-8',
            'X-Requested-With': 'XMLHttpRequest',
        });

        const requestUri = this.getBasePath() + url;
        return this.httpClient.post(requestUri, payload, { headers });
    }

    public submitForm(
        url: string,
        payload: ODataSubmitFormPayload | ODataSubmitParticipantCopyPayload | ODocBoxUploadModel,
        identifier: string,
    ): Observable<unknown> {
        return this.odataFormsService.deepInsertRequest(url, payload, identifier);
    }

    public getBasePath(): string {
        return this.uriUtilities.getUriForContent();
    }
}
